//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion


import { ColumnModel } from '../../modules/api/api.models'
import { Enums } from '../../models/common'

import { LocalizationHelper, Dictionary, TypeHelper, LogHelper, OperatorLayoutSettings } from '../../helpers'
import { KeyType } from '../../helpers/dictionary'

import { OpActivity, OpModule, OpRole, OpOperator } from '../../models/core'
import { IReferenceType } from '../../models/util/interfaces'

import { SELECT_BOX_CONTROLS_SERVICE_NAME, SelectBoxControlsService } from '../controls'
import { GRID_SERVICE_NAME, GridService } from '../grid'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { REFERENCE_TYPE_SERVICE_NAME, ReferenceTypeService } from '../reference-type'

import { OperatorSettingsService, OPERATOR_SETTINGS_SERVICE_NAME } from './operator.settings.service'
import { OperatorService, OPERATOR_SERVICE_NAME } from './operator.service'
import { IMRTabsState } from '../../modules/common/tabs'


//#region Consts

export const OPERATOR_SECURITY_SERVICE_NAME: string = 'OperatorSecurityService'
export const OPERATOR_SECURITY_SERVICE_PROVIDER_NAME: string = 'OperatorSecurityServiceProvider'

//#endregion

export class OperatorSecurityService {

    //#region Consts

    public static get ALLOWED_COLUMN_DATA_FIELD(): string { return 'allow' }
    public static get MODULE_COLUMN_DATA_FIELD(): string { return 'idModule' }

    //#endregion


    //#region Ctor

    constructor(
        private gridService: GridService,
        private operatorService: OperatorService,
        private operatorSettingsService: OperatorSettingsService,
        private referenceTypeService: ReferenceTypeService,
        private selectBoxService: SelectBoxControlsService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Activity

    /**
     * Filters the given activities by the given moduleId.
     * 
     * Activities without a module assigned are always visible.
     */
    public filterActivityByModule(activities: OpActivity[], moduleId: number): OpActivity[] {
        return _.filter(activities, (activity: OpActivity) => {
            return activity.idModule == null || activity.idModule === moduleId
        })
    }

    /**
     * Filters out activities directly assigned to the operator (not from role). 
     */
    public filterCustomActivity(activities: OpActivity[]): OpActivity[] {
        return _.filter(activities, (activity: OpActivity) => {
            return activity.idOperatorActivity != null
        })
    }

    /**
     * Creates a dictionary of operator permission reference types from reference values from the given list of activities.
     */
    public getActivityReferenceTypeDict(activities: OpActivity[]): JQueryPromise<Dictionary<IReferenceType[]>> {
        const referenceDict: Dictionary<number[]> = new Dictionary<number[]>(null, KeyType.Number);

        _.forEach(activities, (activity: OpActivity) => {
            if (activity.idReferenceType != null && activity.referenceValue != null) {
                if (!referenceDict.containsKey(activity.idReferenceType)) {
                    referenceDict.add(activity.idReferenceType, [])
                }

                referenceDict.tryGetValue(activity.idReferenceType).push(activity.referenceValue)
            }
        })

        const promises: JQueryPromise<{ idReferenceType: number; referenceValues: IReferenceType[] }>[] = []

        _.forEach(referenceDict.keys(), (idReferenceType: number) => {
            const referenceValues: number[] = _.uniq(referenceDict.tryGetValue(idReferenceType))
            const referencePromise = this.userSessionService
                .getDataProvider().Operator
                .getOperatorPermissionReferenceObjectList(idReferenceType, referenceValues, null, 1, TypeHelper.MAX_INT_VALUE, TypeHelper.emptyString)
                .then((result: IReferenceType[]) => {
                    return {
                        idReferenceType,
                        referenceValues: result
                    }
                })

            promises.push(referencePromise)
        })

        return $
            .when(...promises)
            .then((...results) => {
                const referenceDict: Dictionary<IReferenceType[]> = new Dictionary<IReferenceType[]>(null);

                _.forEach(results, (result) => {
                    referenceDict.add(result.idReferenceType, result.referenceValues)
                })

                return referenceDict
            })
    }

    /**
     * Returns a string representation of a reference type associated with the given activity.
     */
    public getActivityReferenceTypeString(activityReferenceDict: Dictionary<IReferenceType[]>, activity: OpActivity): string {
        if (activity != null && activity.idReferenceType != null) {
            if (activity.referenceValue != null && _.size(activityReferenceDict.tryGetValue(activity.idReferenceType)) > 0) {
                const activityReferenceType: IReferenceType = _.find(activityReferenceDict.tryGetValue(activity.idReferenceType), (referenceType: IReferenceType) => referenceType.getReferenceKey() === activity.referenceValue)

                return activityReferenceType != null ? activityReferenceType.toString() : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.All)
            }

            return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.All)
        }

        return null
    }

    public loadActivityNavProperties(operator: OpOperator): JQueryPromise<void> {
        const activities: OpActivity[] = operator.activities || []
        const allActivities: OpActivity[] = operator.allActivities || []
        
        return OpActivity
            .loadNavigationProperties(this.userSessionService.getDataProvider(), [...activities, ...allActivities])
            .then(() => { })
    }

    public saveActivitiesToOperator(operator: OpOperator, addedActivities: OpActivity[], modifiedActivities: OpActivity[], deletedActivities: OpActivity[]): JQueryPromise<OpOperator> {
        const operatorToSave = operator.clone()

        if (operator.actor != null) {
            operatorToSave.actor = operator.actor.clone()
            operatorToSave.actor.avatar = null
        }

        operatorToSave.opState = Enums.OpChangeState.Modified
        operatorToSave.allActivities = []

        _.forEach(addedActivities, (addedActivity: OpActivity) => {
            const clonedActivity: OpActivity = addedActivity.clone(true, true)

            clonedActivity.idOperatorActivity = 0
            clonedActivity.opState = Enums.OpChangeState.New

            const activityAleadyExisting: boolean = _.find(operatorToSave.allActivities, (existingActivity: OpActivity) => {
                existingActivity.idOperatorActivity === clonedActivity.idOperatorActivity
            }) != null

            if (!activityAleadyExisting) {
                operatorToSave.allActivities.push(clonedActivity)
            }
        })

        _.forEach(modifiedActivities, (modifiedActivity: OpActivity) => {
            const clonedActivity: OpActivity = modifiedActivity.clone(true, true)

            clonedActivity.opState = Enums.OpChangeState.Modified

            operatorToSave.allActivities.push(clonedActivity)
        })

        _.forEach(deletedActivities, (deletedActivity: OpActivity) => {
            const clonedActivity: OpActivity = deletedActivity.clone(true, true)

            clonedActivity.opState = Enums.OpChangeState.Delete

            operatorToSave.allActivities.push(clonedActivity)
        })

        const activityIds: number[] = [...modifiedActivities, ...deletedActivities].map((deletedActivity: OpActivity) => deletedActivity.idOperatorActivity)

        operator.allActivities
            .filter((activity: OpActivity) => !_.include(activityIds, activity.idOperatorActivity))
            .forEach((loadedActivity: OpActivity) => {
                const clonedActivity: OpActivity = loadedActivity.clone(true, true)

                clonedActivity.opState = Enums.OpChangeState.Loaded

                operatorToSave.allActivities.push(clonedActivity)
            })

        return this.operatorService.saveOperator(operatorToSave)
    }

    //#endregion


    //#region Columns

    public getActivityColumns(): ColumnModel[] {
        return [
            this.getDescriptionColumn(),
            this.getAllowColumn(),
            this.getReferenceTypeColumn(),
            this.getReferenceValueColumn()
        ]
    }

    public getSelectCustomActivityColumns(): JQueryPromise<ColumnModel[]> {
        return $.Deferred().resolve([this.getDescriptionColumn(), this.getAllowColumn()]).promise()
    }

    public getCustomActivityColumns(
        modules: OpModule[],
        onRemoveReferenceClick: (activity: OpActivity) => void,
        onEditReferenceClick: (activity: OpActivity, buttonEl: JQuery) => void,
        allowEditing: boolean = false
    ): JQueryPromise<ColumnModel[]> {
        const columnsPromises: ColumnModel[] = [
            this.getDescriptionColumn(),
            this.getAllowColumn(allowEditing),
            this.getReferenceTypeColumn(),
            this.getModuleColumn(modules, allowEditing),
            this.getDeleteReferenceColumn(onRemoveReferenceClick, allowEditing),
        ]

        if (allowEditing) {
            columnsPromises.push(this.getEditReferenceColumn(onEditReferenceClick))
        }

        return $
            .when<any>(...columnsPromises)
            .then((...columns: ColumnModel[]) => {
                return [...columns]
            })
    }

    public getRoleColumns(): JQueryPromise<ColumnModel[]> {
        const nameColumn = new ColumnModel()
        const descriptionColumn = new ColumnModel()
        const moduleColumn = new ColumnModel()

        nameColumn.idName = 'name'
        nameColumn.type = Enums.DataTypeClass.Text
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)

        descriptionColumn.idName = 'descr'
        descriptionColumn.type = Enums.DataTypeClass.Text
        descriptionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)

        moduleColumn.idName = 'module'
        moduleColumn.type = Enums.DataTypeClass.Text
        moduleColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Module)

        return $.Deferred().resolve([nameColumn, descriptionColumn, moduleColumn]).promise()
    }

    private getAllowColumn(isEditable: boolean = false): ColumnModel {
        const allowColumn = new ColumnModel()

        allowColumn.allowEditing = isEditable
        allowColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Allow)
        allowColumn.idName = OperatorSecurityService.ALLOWED_COLUMN_DATA_FIELD
        allowColumn.type = Enums.DataTypeClass.Boolean
        allowColumn.width = 150

        return allowColumn
    }

    private getDescriptionColumn(): ColumnModel {
        const descriptionColumn = new ColumnModel()

        descriptionColumn.idName = "toString"
        descriptionColumn.type = Enums.DataTypeClass.Text
        descriptionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)

        return descriptionColumn
    }

    private getEditReferenceColumn(onClick: (activity: OpActivity, buttonEl: JQuery) => void): ColumnModel {
        const toolbarColumn: ColumnModel = this.gridService.getToolbarColumn<OpActivity>((gridObject: any) => {
            const activity: OpActivity = this.gridService.getSourceObjectFromGridObject<OpActivity>(gridObject, Enums.ReferenceType.IdActivity)

            if (activity != null && activity.idReferenceType != null) {
                return {
                    items: [{
                        location: 'center',
                        widget: 'dxButton',
                        options: {
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditReferenceObject),
                            icon: 'edit',

                            onClick: (e: any) => {
                                onClick(activity, e.element)
                            }
                        }
                    }]
                }
            }

            return null
        });

        toolbarColumn.name = 'editReferenceType'
        toolbarColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditReferenceObject)
        toolbarColumn.width = 70

        return toolbarColumn
    }

    private getModuleColumn(modules: OpModule[], isEditable: boolean = false): ColumnModel {
        const moduleColumn = new ColumnModel()

        moduleColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Module)

        if (isEditable) {
            moduleColumn.idName = OperatorSecurityService.MODULE_COLUMN_DATA_FIELD
            moduleColumn.editCellTemplate = {
                isLookup: true,
                content: {
                    dataSource: modules,
                    displayExpr: "toString",
                    valueExpr: "idModule"
                }
            }
        } else {
            moduleColumn.idName = 'moduleName'
        }

        return moduleColumn
    }

    private getReferenceTypeColumn(): ColumnModel {
        const referenceTypeColumn = new ColumnModel()

        referenceTypeColumn.idName = "referenceType"
        referenceTypeColumn.type = Enums.DataTypeClass.Text
        referenceTypeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReferenceType)

        return referenceTypeColumn
    }

    private getReferenceValueColumn(): ColumnModel {
        const referenceValueColumn = new ColumnModel()

        referenceValueColumn.idName = 'referenceValue'
        referenceValueColumn.type = Enums.DataTypeClass.Text
        referenceValueColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReferenceObject)

        return referenceValueColumn
    }

    private getDeleteReferenceColumn(onClick: (activity: OpActivity) => void, isEditable: boolean = false): ColumnModel {
        const toolbarColumn: ColumnModel = this.gridService.getToolbarColumn<OpActivity>((gridObject: any) => {
            const activity: OpActivity = this.gridService.getSourceObjectFromGridObject<OpActivity>(gridObject, Enums.ReferenceType.IdActivity)

            if (activity != null && activity.idReferenceType != null) {
                const toolbarItems: any[] = []

                if (activity.referenceValue != null) {
                    toolbarItems.push({
                        location: 'before',
                        text: gridObject.referenceName,
                    })

                    if (isEditable) {
                        toolbarItems.push({
                            location: 'after',
                            widget: 'dxButton',
                            options: {
                                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DeleteReferenceObject),
                                icon: 'remove',

                                onClick: (e: any) => {
                                    onClick(activity)
                                }
                            }
                        })
                    }

                } else {
                    toolbarItems.push({
                        location: 'before',
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.All)
                    })
                }

                return {
                    items: toolbarItems
                }
            }

            return null
        });

        toolbarColumn.name = 'referenceValue'
        toolbarColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReferenceObject)
        toolbarColumn.width = 200

        return toolbarColumn
    }

    //#endregion


    //#region Module

    public getActivityModuleString(modules: OpModule[], activity: OpActivity): string {
        const activityModule: OpModule = _.find(modules, (module: OpModule) => {
            return module.idModule === activity.idModule
        })

        return activityModule != null ? activityModule.toString() : null
    }

    /**
     * Returns a list of modules with an All fake module as well (if logged operator has all module access).
     */
    public getCombinedModules(): JQueryPromise<OpModule[]> {
        return $
            .when<any>(
                this.referenceTypeService.getDictionaryReferenceObjectList(Enums.ReferenceType.IdModule),
                this.hasAllModuleAccess()
            )
            .then((modules: OpModule[], hasAllModuleAccess: boolean) => {
                const combinedModules: OpModule[] = []

                if (hasAllModuleAccess) {
                    combinedModules.push(this.getAllModule())
                }

                combinedModules.push(...modules)

                return combinedModules
            })
    }

    /**
     * Retuns the current saved module from operator settings.
     */
    public getCurrentModuleId(): number {
        return OperatorLayoutSettings.getSetting<number>(OperatorLayoutSettings.SecurityModuleId, null)
    }

    public getModuleSelectBoxOptions(modules: OpModule[]): DevExpress.ui.dxSelectBoxOptions {
        return this.selectBoxService.getSelectBoxOptions({
            items: modules
        })
    }

    public saveCurrentModuleId(moduleId: number): void {
        OperatorLayoutSettings.setSetting(OperatorLayoutSettings.SecurityModuleId, moduleId)
    }

    private getAllModule(): OpModule {
        const module: OpModule = new OpModule(null)

        module.name = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AllModules)
        module.idModule = null

        return module
    }

    private hasAllModuleAccess(): JQueryPromise<boolean> {
        return this.operatorSettingsService
            .hasDictionaryCompleteness(Enums.ReferenceType.IdModule)
            .fail((error: any) => {
                LogHelper.warn(OperatorService.LOG_PREFIX, 'Unable to load has dictionary completness from dict.', error)

                return false
            })
    }

    //#endregion


    //#region Role

    public getAvailableRoles(): JQueryPromise<OpRole[]> {
        return this.referenceTypeService.getDictionaryReferenceObjectList<OpRole>(Enums.ReferenceType.IdRole)
    }

    public saveRolesToOperator(operator: OpOperator, addedRoles: OpRole[], deletedRoles: OpRole[]): JQueryPromise<OpOperator> {
        const operatorToSave = operator.clone()

        if (operator.actor != null) {
            operatorToSave.actor = operator.actor.clone()
            operatorToSave.actor.avatar = null
        }

        operatorToSave.opState = Enums.OpChangeState.Modified
        operatorToSave.roles = []

        _.forEach(addedRoles, (addedRole: OpRole) => {
            const clonedRole = addedRole.clone(true, true)

            clonedRole.opState = Enums.OpChangeState.New

            const roleAleadyExisting: boolean = _.find(operatorToSave.roles, (existingRole: OpRole) => {
                existingRole.idRole === clonedRole.idRole
            }) != null

            if (!roleAleadyExisting) {
                operatorToSave.roles.push(clonedRole)
            }
        })

        _.forEach(deletedRoles, (deletedRole: OpRole) => {
            const clonedRole = deletedRole.clone(true, true)

            clonedRole.opState = Enums.OpChangeState.Delete

            operatorToSave.roles.push(clonedRole)
        })

        const roleIds: number[] = [...deletedRoles].map((role: OpRole) => role.idRole)

        operator.roles
            .filter((roles: OpRole) => !_.include(roleIds, roles.idRole))
            .forEach((loadedRole: OpRole) => {
                const clonedRole: OpRole = loadedRole.clone(true, true)

                clonedRole.opState = Enums.OpChangeState.Loaded

                operatorToSave.roles.push(clonedRole)
            })

        return this.operatorService.saveOperator(operatorToSave)
    }

    //#endregion


    //#region Tabs

    public restoreTabState(): IMRTabsState {
        return {
            selectedTabIndex: OperatorLayoutSettings.getSetting<number>(OperatorLayoutSettings.UserPopupSecurityTabActive)
        }
    }

    public saveTabState(state: IMRTabsState) {
        OperatorLayoutSettings.setSetting(OperatorLayoutSettings.UserPopupSecurityTabActive, state.selectedTabIndex)
    }

    //#endregion
}

export class OperatorSecurityServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        GRID_SERVICE_NAME,
        OPERATOR_SERVICE_NAME,
        OPERATOR_SETTINGS_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        SELECT_BOX_CONTROLS_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            gridService: GridService,
            operatorService: OperatorService,
            operatorSettingsService: OperatorSettingsService,
            referenceTypeService: ReferenceTypeService,
            selectBoxService: SelectBoxControlsService,
            userSessionService: UserSessionService
        ) {
            return new OperatorSecurityService(gridService,
                operatorService,
                operatorSettingsService,
                referenceTypeService,
                selectBoxService,
                userSessionService
            )
        }]

    //#endregion
} 